import React, { Fragment, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

import NoData from "./components/NoData";
import Pagination from "./components/Pagination";
import AllTicketsLoading from "./AllTicketsLoading";

import "./style.scss";

/**
 * Tickets listing table
 * @param {Object} props
 * @param {Array} props.state
 * @param {Array} props.state.data
 * @param {Array} props.state.columns
 * @param {Object} props?.rowSelection
 * @param {Object} props?.columnVisibility
 * @param {Function} props?.handleRowSelection
 */
const Table = observer((props) => {
  const data = props?.state?.data || props?.data;
  const columns = props?.state?.columns || props.columns;
  const noData = !data.loading && !data?.data?.length;
  const showPagination = props.pagination !== false;
  const table = useReactTable({
    data: data.data,
    manualPagination: true,
    enableRowSelection: !!props?.rowSelection,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    columns: columns,
    pageCount: data.pageCount,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: props.actions?.setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: props?.handleRowSelection,
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    manualSorting: true,
    onSortingChange: props.actions?.setSorting,
    state: {
      sorting: props?.data?.sorting,
      rowSelection: props?.rowSelection || {},
      columnVisibility: props?.columnVisibility,
      pagination: {
        pageIndex: data.pageIndex,
        pageSize: data.pageSize,
      },
    },
  });
  return (
    <Fragment>
      <AllTicketsLoading loading={data.loading} />
      <div className={`table-container ${noData ? "d-none" : ""}`}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      style={{ position: "relative", width: header.getSize() }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            <div
                              className={
                                header.column.getCanSort()
                                  ? "cursor-pointer select-none"
                                  : ""
                              }
                              onClick={header.column.getToggleSortingHandler()}
                              title={
                                header.column.getCanSort()
                                  ? header.column.getNextSortingOrder() ===
                                    "asc"
                                    ? "Sort ascending"
                                    : header.column.getNextSortingOrder() ===
                                      "desc"
                                    ? "Sort descending"
                                    : "Clear sort"
                                  : undefined
                              }
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: " 🔼",
                                desc: " 🔽",
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`resizer ${
                            header.column.getIsResizing() ? "isResizing" : ""
                          }`}
                        ></div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const rowClasses = `${row?.getIsSelected() ? "selected" : ""}`;
              return (
                <tr key={row?.id} className={rowClasses}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell?.id}>
                        {flexRender(
                          cell?.column.columnDef?.cell,
                          cell?.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showPagination && (
        noData ? (
          <NoData />
        ) : (
          <Pagination
            table={{
              ...table,
              to: data.to,
              from: data.from,
              loading: data.loading,
              totalRecords: data.total,
              pageIndex: data.pageIndex,
              pageCount: data.pageCount,
            }}
          />
        )
      )}
    </Fragment>
  );
});
export default Table;
